/**
 * useWishlist — CRUD operations for user wishlists and wishlist items.
 */
import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
import { useMilestoneAwarder } from '@/hooks/useMilestoneAwarder';
import { Wishlist, WishlistItem, WishlistOccasion } from '@/types/wishlist';
import { logger } from '@/utils/logger';

export function useWishlist() {
  const { user } = useAuth();
  const { toast } = useToast();
  const queueMilestone = useMilestoneAwarder();
  const [wishlists, setWishlists] = useState<Wishlist[]>([]);
  const [items, setItems] = useState<WishlistItem[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchWishlists = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('wishlists')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: true });
      if (error) throw error;
      setWishlists((data as any[]) || []);
    } catch (e: any) {
      toast({ title: 'Error loading wishlists', description: e.message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [user, toast]);

  const fetchItems = useCallback(async (wishlistId: string) => {
    try {
      const { data, error } = await supabase
        .from('wishlist_items')
        .select('*')
        .eq('wishlist_id', wishlistId)
        .order('priority', { ascending: false });
      if (error) throw error;
      setItems((data as any[]) || []);
    } catch (e: any) {
      toast({ title: 'Error loading items', description: e.message, variant: 'destructive' });
    }
  }, [toast]);

  const createWishlist = useCallback(async (name: string, occasion: WishlistOccasion = 'general', isPublic = true, eventDate?: string | null, paymentDetails?: string | null) => {
    if (!user) return null;
    try {
      const insertData: any = { user_id: user.id, name, occasion, is_public: isPublic };
      if (eventDate) insertData.event_date = eventDate;
      if (paymentDetails) insertData.payment_details = paymentDetails;
      const { data, error } = await supabase
        .from('wishlists')
        .insert(insertData)
        .select()
        .single();
      if (error) throw error;
      setWishlists(prev => [...prev, data as any]);
      toast({ title: 'Wishlist created!' });
      return data as unknown as Wishlist;
    } catch (e: any) {
      toast({ title: 'Error creating wishlist', description: e.message, variant: 'destructive' });
      return null;
    }
  }, [user, toast]);

  const deleteWishlist = useCallback(async (id: string) => {
    try {
      const { error } = await supabase.from('wishlists').delete().eq('id', id);
      if (error) throw error;
      setWishlists(prev => prev.filter(w => w.id !== id));
      toast({ title: 'Wishlist deleted' });
    } catch (e: any) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    }
  }, [toast]);

  const updateWishlist = useCallback(async (id: string, updates: Partial<Pick<Wishlist, 'event_date' | 'payment_details' | 'name' | 'occasion' | 'is_public'>>) => {
    try {
      const { data, error } = await supabase
        .from('wishlists')
        .update(updates as any)
        .eq('id', id)
        .select()
        .single();
      if (error) throw error;
      setWishlists(prev => prev.map(w => w.id === id ? (data as any) : w));
      toast({ title: 'List updated' });
      return data as unknown as Wishlist;
    } catch (e: any) {
      toast({ title: 'Error updating list', description: e.message, variant: 'destructive' });
      return null;
    }
  }, [toast]);

  const addItem = useCallback(async (wishlistId: string, item: Partial<WishlistItem>) => {
    if (!user) return null;
    try {
      const { data, error } = await supabase
        .from('wishlist_items')
        .insert({ ...item, wishlist_id: wishlistId, user_id: user.id } as any)
        .select()
        .single();
      if (error) throw error;
      setItems(prev => [...prev, data as any]);
      toast({ title: 'Item added to wishlist!' });
      queueMilestone('first_wishlist_item').catch(() => {});
      return data as unknown as WishlistItem;
    } catch (e: any) {
      toast({ title: 'Error adding item', description: e.message, variant: 'destructive' });
      return null;
    }
  }, [user, toast]);

  const updateItem = useCallback(async (itemId: string, updates: Partial<WishlistItem>) => {
    try {
      const { data, error } = await supabase
        .from('wishlist_items')
        .update(updates as any)
        .eq('id', itemId)
        .select()
        .single();
      if (error) throw error;
      setItems(prev => prev.map(i => i.id === itemId ? (data as any) : i));
      return data as unknown as WishlistItem;
    } catch (e: any) {
      toast({ title: 'Error updating item', description: e.message, variant: 'destructive' });
      return null;
    }
  }, [toast]);

  const deleteItem = useCallback(async (itemId: string) => {
    try {
      const { error } = await supabase.from('wishlist_items').delete().eq('id', itemId);
      if (error) throw error;
      setItems(prev => prev.filter(i => i.id !== itemId));
      toast({ title: 'Item removed' });
    } catch (e: any) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    }
  }, [toast]);

  const reserveItem = useCallback(async (itemId: string, reserverName?: string) => {
    if (!user) return false;
    try {
      const { error } = await supabase
        .from('wishlist_items')
        .update({ status: 'reserved', reserved_by: user.id, reserved_by_name: reserverName || user.email } as any)
        .eq('id', itemId);
      if (error) throw error;
      setItems(prev => prev.map(i => i.id === itemId ? { ...i, status: 'reserved' as const, reserved_by: user.id } : i));
      toast({ title: "You've reserved this gift!" });
      return true;
    } catch (e: any) {
      toast({ title: 'Error reserving item', description: e.message, variant: 'destructive' });
      return false;
    }
  }, [user, toast]);

  const contributeToItem = useCallback(async (itemId: string, amount: number, contributorName: string, currency: string = 'AED') => {
    if (!user) return false;
    try {
      const { error } = await supabase
        .from('wishlist_contributions')
        .insert({
          wishlist_item_id: itemId,
          contributor_id: user.id,
          contributor_name: contributorName,
          amount,
          currency,
        } as any);
      if (error) throw error;
      toast({ title: '🎉 Contribution recorded!', description: `You chipped in ${currency} ${amount}` });
      return true;
    } catch (e: any) {
      toast({ title: 'Error contributing', description: e.message, variant: 'destructive' });
      return false;
    }
  }, [user, toast]);

  useEffect(() => { fetchWishlists(); }, [fetchWishlists]);

  return {
    wishlists, items, loading,
    fetchWishlists, fetchItems,
    createWishlist, deleteWishlist, updateWishlist,
    addItem, updateItem, deleteItem,
    reserveItem, contributeToItem,
  };
}

// Hook for public wishlist viewing (no auth required)
export function usePublicWishlist(shareToken: string | undefined) {
  const [wishlist, setWishlist] = useState<Wishlist | null>(null);
  const [items, setItems] = useState<WishlistItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [ownerName, setOwnerName] = useState<string>('');

  useEffect(() => {
    if (!shareToken) return;
    const fetch = async () => {
      setLoading(true);
      try {
        // Token-gated SECURITY DEFINER reads: the wishlist row never carries
        // `payment_details` and items never carry `reserved_by_phone`.
        const { data: wlRows, error: wlErr } = await supabase
          .rpc('get_public_wishlist' as any, { p_share_token: shareToken });
        const wl = Array.isArray(wlRows) ? wlRows[0] : wlRows;
        if (wlErr || !wl) { setLoading(false); return; }

        // Payment details are fetched separately (also token-gated) so gifting
        // instructions keep working for guests.
        const { data: pay } = await supabase
          .rpc('get_public_wishlist_payment_details' as any, { p_share_token: shareToken });
        setWishlist({ ...(wl as any), payment_details: pay ?? null } as any);

        // Get owner name via RPC (bypasses RLS for unauthenticated users)
        const { data: ownerDisplayName } = await supabase
          .rpc('get_wishlist_owner_name', { p_share_token: shareToken });
        setOwnerName(ownerDisplayName || 'Someone');

        const { data: itms } = await supabase
          .rpc('get_public_wishlist_items' as any, { p_share_token: shareToken });
        setItems((itms as any[]) || []);
      } catch (e) {
        logger.error('Error loading public wishlist', e);
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [shareToken]);


  return { wishlist, items, loading, ownerName };
}
